import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Pencil, RotateCw, History } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Iter53 — Editable payroll line breakdown.
 * Shows base_pay, overtime_pay, deductions_breakdown, gross_pay, net_pay.
 * Each editable component has a pencil that opens an override modal requiring
 * `reason`. Also renders a "Recompute from attendance" button and an audit tab.
 *
 * Response-shape note (do NOT rename these):
 *   - Deduction rows use `{label, amount, note?}` (not line_type/formula_hint).
 *   - Manual overrides live under `line.manual_overrides[field_key]` where
 *     field_key is "base_pay" | "overtime_pay" | "weekend_holiday_pay" |
 *     "deduction:<label>". Each entry is `{old, new, reason, actor, at}`.
 *   - `net_pay` is DERIVED — the backend override whitelist rejects it
 *     intentionally. Users override components; net follows.
 */
export default function PayrollLineBreakdown({ line, canEdit, canRecompute, onChanged }) {
    const [overrideOpen, setOverrideOpen] = useState(false);
    const [overrideField, setOverrideField] = useState(null);
    const [overrideLabel, setOverrideLabel] = useState("");
    const [overrideCurrent, setOverrideCurrent] = useState(0);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [history, setHistory] = useState([]);
    const [recomputing, setRecomputing] = useState(false);
    const [recomputeConfirmOpen, setRecomputeConfirmOpen] = useState(false);

    if (!line) return null;

    const overrides = line.manual_overrides || {};
    const isOverridden = (field) => !!overrides[field];

    const openOverride = (field, label, current) => {
        setOverrideField(field);
        setOverrideLabel(label);
        setOverrideCurrent(current);
        setOverrideOpen(true);
    };

    const loadHistory = async () => {
        try {
            const { data } = await api.get(`/payroll/lines/${line.id}/audit`);
            setHistory(data.history || []);
            setHistoryOpen(true);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const recompute = async () => {
        const overrideCount = Object.keys(overrides).length;
        if (overrideCount > 0 && !recomputeConfirmOpen) {
            setRecomputeConfirmOpen(true);
            return;
        }
        setRecomputing(true);
        try {
            await api.post(`/payroll/lines/${line.id}/recompute`);
            toast.success("Recomputed from attendance");
            setRecomputeConfirmOpen(false);
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRecomputing(false); }
    };

    const base = line.base_pay ?? line.gross_regular ?? 0;
    const overtime = line.overtime_pay ?? line.gross_overtime ?? 0;
    const whUplift = line.weekend_holiday_pay ?? line.gross_weekend_holiday_uplift ?? 0;
    const gross = line.gross_pay ?? line.gross_total ?? 0;
    const net = line.net_pay ?? line.net_payable ?? 0;
    const dedRows = line.deductions_breakdown || line.deductions || [];

    return (
        <div className="space-y-4" data-testid={`payroll-breakdown-${line.employee_code}`}>
            <div className="flex items-center justify-between">
                <h4 className="font-semibold asl-navy-text text-sm">Pay breakdown</h4>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={loadHistory} data-testid="breakdown-history-btn">
                        <History className="w-3.5 h-3.5 mr-1" />
                        Audit history
                    </Button>
                    {canRecompute && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={recompute}
                            disabled={recomputing}
                            data-testid="breakdown-recompute-btn"
                        >
                            <RotateCw className={`w-3.5 h-3.5 mr-1 ${recomputing ? "animate-spin" : ""}`} />
                            Recompute
                        </Button>
                    )}
                </div>
            </div>

            <div className="border rounded-md overflow-hidden">
                <BreakdownRow
                    label="Base pay"
                    amount={base}
                    tone="positive"
                    overridden={isOverridden("base_pay")}
                    canEdit={canEdit}
                    onEdit={() => openOverride("base_pay", "Base pay", base)}
                    tooltip={line.wage_rate_snapshot?.payment_frequency
                        ? `Frequency: ${line.wage_rate_snapshot.payment_frequency}`
                        : ""}
                    testid="breakdown-base"
                />
                <BreakdownRow
                    label="Overtime pay"
                    amount={overtime}
                    tone="positive"
                    overridden={isOverridden("overtime_pay")}
                    canEdit={canEdit}
                    onEdit={() => openOverride("overtime_pay", "Overtime pay", overtime)}
                    testid="breakdown-overtime"
                />
                {/* Iter61.B.5 — Per-entry OT lines. Each authorized entry
                    surfaces as its own line so managers see the multiplier,
                    rate, project, authorized-by, and reason. */}
                {(line.ot_line_items || []).map((it, i) => (
                    <OTEntryLine key={i} item={it} testid={`breakdown-ot-entry-${i}`} />
                ))}
                {/* Iter63 — Schedule bonus block. Rendered under the OT
                    lines when the payroll line snapshot carries any
                    per-day-override bonus items. Each bonus day is its
                    own auditable row at 1× with the rule-configured label. */}
                {(line.schedule_bonus_lines || []).length > 0 && (
                    <BreakdownRow
                        label="Schedule bonus"
                        amount={Number(line.schedule_bonus_amount) || 0}
                        tone="positive"
                        overridden={false}
                        canEdit={false}
                        testid="breakdown-schedule-bonus-total"
                    />
                )}
                {(line.schedule_bonus_lines || []).map((it, i) => (
                    <ScheduleBonusLine
                        key={`bonus-${i}`}
                        item={it}
                        testid={`breakdown-schedule-bonus-entry-${i}`}
                    />
                ))}
                {whUplift > 0 && (
                    <BreakdownRow
                        label="Weekend / holiday uplift"
                        amount={whUplift}
                        tone="positive"
                        overridden={isOverridden("weekend_holiday_pay")}
                        canEdit={canEdit}
                        onEdit={() => openOverride("weekend_holiday_pay", "Weekend/holiday uplift", whUplift)}
                        testid="breakdown-weekend-holiday"
                    />
                )}
                <div className="border-t bg-slate-50 px-3 py-1.5 flex items-center justify-between text-xs">
                    <span className="font-medium">Gross pay</span>
                    <span className="mono font-semibold asl-navy-text">{fmt(gross)}</span>
                </div>
            </div>

            {dedRows.length > 0 && (
                <div className="border rounded-md overflow-hidden bg-rose-50/40">
                    <div className="px-3 py-1.5 text-xs font-medium text-rose-700 border-b bg-rose-100/60">Deductions</div>
                    {dedRows.map((d, i) => {
                        const key = `deduction:${d.label}:${i}`;
                        // Iter60.p25 — prefer explicit `reason` over generic
                        // notes. Falls back cleanly for legacy rows.
                        const subtitle = d.reason || d.note || d.notes;
                        return (
                            <BreakdownRow
                                key={key}
                                label={d.label}
                                amount={-Math.abs(d.amount)}
                                tone="negative"
                                overridden={isOverridden(`deduction:${d.label}`) || !!d.overridden_reason}
                                overrideReason={d.overridden_reason}
                                canEdit={canEdit}
                                onEdit={() => openOverride(`deduction:${d.label}`, `Deduction — ${d.label}`, Math.abs(d.amount))}
                                note={subtitle}
                                testid={`breakdown-ded-${d.label.toLowerCase().replace(/\s+/g, "-")}-${i}`}
                            />
                        );
                    })}
                </div>
            )}

            <div className="border-2 border-emerald-200 bg-emerald-50 rounded-md px-3 py-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-emerald-900">Net pay</span>
                <span className="mono text-lg font-bold text-emerald-900" data-testid="breakdown-net">{fmt(net)}</span>
            </div>

            {line.attendance_summary && (
                <>
                    {/* Iter59.p13 — Absent-days info row. Absent days are NOT
                        deducted from pay; they simply don't contribute to
                        base_pay in the first place. Called out explicitly
                        here so admins can reconcile pay against attendance. */}
                    {line.attendance_summary.absent_days > 0 && (
                        <div
                            className="text-[11px] px-2 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-amber-900 mono"
                            data-testid="breakdown-absent-info"
                        >
                            <span className="font-semibold">Absent days: {line.attendance_summary.absent_days}</span>
                            {" · no pay for absent days · no deduction"}
                        </div>
                    )}
                    {/* Iter59.p14 — Holiday-worked info. Days that landed on
                        an active public holiday get the shift rule's
                        `holiday_rate_multiplier` (holiday > weekend > weekday). */}
                    {line.attendance_summary.holiday_worked_days > 0 && (
                        <div
                            className="text-[11px] px-2 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 mono"
                            data-testid="breakdown-holiday-uplift"
                        >
                            <span className="font-semibold">Worked on holiday: {line.attendance_summary.holiday_worked_days} day(s)</span>
                            {" · paid at "}{line.attendance_summary.holiday_multiplier || 1}{"× (shift rule)"}
                        </div>
                    )}
                    <div className="text-[11px] text-[color:var(--asl-muted)] mono">
                        Days: {line.attendance_summary.total_days} · Present/Late: {line.attendance_summary.present_days} · Absent: {line.attendance_summary.absent_days} · Paid leave: {line.attendance_summary.paid_leave_days} · Unpaid leave: {line.attendance_summary.unpaid_leave_days} · Holiday: {line.attendance_summary.holiday_days} · Excused early: {line.attendance_summary.excused_early_days || 0} · Shift ref: {line.attendance_summary.shift_minutes_ref || 480}m
                    </div>
                </>
            )}

            <OverrideDialog
                open={overrideOpen}
                onOpenChange={setOverrideOpen}
                lineId={line.id}
                field={overrideField}
                label={overrideLabel}
                currentValue={overrideCurrent}
                onSaved={() => { setOverrideOpen(false); onChanged?.(); }}
            />
            <HistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} history={history} />

            {/* Iter53 Bug B — Recompute confirmation dialog (replaces window.confirm) */}
            <Dialog open={recomputeConfirmOpen} onOpenChange={setRecomputeConfirmOpen}>
                <DialogContent data-testid="breakdown-recompute-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle>Discard manual adjustments?</DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-slate-700">
                        This line has <strong>{Object.keys(overrides).length}</strong> manual override(s). Recomputing will discard them and pull fresh values from attendance.
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRecomputeConfirmOpen(false)}>Cancel</Button>
                        <Button
                            className="asl-crimson-bg text-white"
                            onClick={recompute}
                            disabled={recomputing}
                            data-testid="breakdown-recompute-confirm"
                        >
                            {recomputing ? "Recomputing…" : "Recompute anyway"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function BreakdownRow({ label, amount, tone, overridden, overrideReason, canEdit, onEdit, note, tooltip, testid }) {
    const color = tone === "negative" ? "text-rose-800" : "asl-navy-text";
    return (
        <div className="px-3 py-2 border-b last:border-b-0 flex items-center justify-between text-xs" data-testid={testid}>
            <div className="flex items-center gap-2">
                <span className="font-medium">{label}</span>
                {overridden && (
                    <span
                        className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-medium"
                        title={overrideReason ? `Overridden: ${overrideReason}` : "Manually adjusted"}
                        data-testid={`${testid}-override-chip`}
                    >
                        <Pencil className="w-2.5 h-2.5" /> edited
                    </span>
                )}
                {note && <span className="text-[10px] text-[color:var(--asl-muted)]">({note})</span>}
                {tooltip && <span className="text-[10px] text-[color:var(--asl-muted)]">{tooltip}</span>}
            </div>
            <div className="flex items-center gap-2">
                <span className={`mono ${color}`}>{fmt(amount)}</span>
                {canEdit && (
                    <button
                        type="button"
                        className="p-1 rounded hover:bg-slate-200 text-slate-500"
                        onClick={onEdit}
                        title="Override"
                        data-testid={`${testid}-edit-btn`}
                    >
                        <Pencil className="w-3 h-3" />
                    </button>
                )}
            </div>
        </div>
    );
}


function OverrideDialog({ open, onOpenChange, lineId, field, label, currentValue, onSaved }) {
    const [newValue, setNewValue] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) { setNewValue(String(currentValue ?? "")); setReason(""); }
    }, [open, currentValue]);

    const save = async () => {
        const parsed = parseFloat(newValue);
        if (Number.isNaN(parsed) || parsed < 0) { toast.error("Enter a non-negative number"); return; }
        if (!reason || reason.trim().length < 3) { toast.error("A reason (min 3 chars) is required"); return; }
        setSaving(true);
        try {
            await api.patch(`/payroll/lines/${lineId}/override`, {
                field, new_value: parsed, reason: reason.trim(),
            });
            toast.success(`${label} overridden`);
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="line-override-dialog">
                <DialogHeader>
                    <DialogTitle>Override {label}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="text-xs text-[color:var(--asl-muted)]">
                        Current: <span className="mono">{fmt(currentValue)}</span>
                    </div>
                    <div>
                        <Label>New amount (USD)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={newValue}
                            onChange={(e) => setNewValue(e.target.value)}
                            data-testid="override-new-value"
                        />
                    </div>
                    <div>
                        <Label>Reason (required)</Label>
                        <Textarea
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Manual correction — approved by HR"
                            data-testid="override-reason"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button className="asl-navy-bg text-white" onClick={save} disabled={saving} data-testid="override-save">
                        {saving ? "Saving…" : "Save override"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function HistoryDialog({ open, onOpenChange, history }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="line-audit-dialog">
                <DialogHeader>
                    <DialogTitle>Audit history</DialogTitle>
                </DialogHeader>
                <div className="max-h-96 overflow-y-auto">
                    {history.length === 0 ? (
                        <div className="text-xs text-[color:var(--asl-muted)] p-4 text-center">
                            No manual edits yet.
                        </div>
                    ) : (
                        <table className="w-full text-xs asl-table">
                            <thead>
                                <tr>
                                    <th className="text-left">When</th>
                                    <th className="text-left">Who</th>
                                    <th className="text-left">Field</th>
                                    <th className="text-right">Old</th>
                                    <th className="text-right">New</th>
                                    <th className="text-left">Reason</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((h, i) => (
                                    <tr key={h.id} data-testid={`audit-row-${i}`}>
                                        <td className="mono text-[10px]">{h.edited_at?.slice(0, 19).replace("T", " ")}</td>
                                        <td>{h.actor_name || h.actor_email}</td>
                                        <td className="mono">{h.field}</td>
                                        <td className="text-right mono">{typeof h.old_value === "number" ? fmt(h.old_value) : "—"}</td>
                                        <td className="text-right mono">{typeof h.new_value === "number" ? fmt(h.new_value) : "—"}</td>
                                        <td className="text-[11px]">{h.reason}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// -----------------------------------------------------------------------
// Iter61.B.5 — Per-entry OT breakdown line.
//
// Rendered as a nested row directly under "Overtime pay". Format:
//   "OT — {h}h {m}m @ ${rate}/hr × {mult} = ${amount}"
// Caption line:
//   "Project: {name} • Authorized by {name} on {date} • Reason: {reason}"
// Hours+minutes are ALWAYS both rendered, even when one is zero, so the
// UX is consistent (e.g. "1h 0m", "0h 45m").
// -----------------------------------------------------------------------
function fmtHM(minutes) {
    const m = Math.max(0, Math.round(Number(minutes) || 0));
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return `${h}h ${rem}m`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        });
    } catch {
        return String(iso).slice(0, 10);
    }
}

function OTEntryLine({ item, testid }) {
    const mins = Number(item.minutes) || 0;
    const rate = Number(item.hourly_rate) || 0;
    const mult = Number(item.multiplier) || 0;
    const amount = Number(item.amount) || 0;
    const proj = item.project_name || "—";
    const by = item.authorized_by_name || "—";
    const when = fmtDate(item.authorized_at || item.work_date);
    return (
        <div
            className="border-t px-3 py-1.5 bg-slate-50/40 text-[11px] leading-tight"
            data-testid={testid}
        >
            <div className="flex items-center justify-between mono">
                <span className="text-slate-700">
                    OT — <strong>{fmtHM(mins)}</strong>
                    {" @ "}${rate.toFixed(2)}/hr × {mult.toFixed(2)}
                </span>
                <span className="asl-navy-text font-semibold">= {fmt(amount)}</span>
            </div>
            <div className="text-[10px] text-[color:var(--asl-muted)] mt-0.5">
                Project: <span className="asl-navy-text">{proj}</span>
                <span className="mx-1.5">•</span>
                Authorized by <span className="asl-navy-text">{by}</span> on {when}
                {item.reason ? (
                    <>
                        <span className="mx-1.5">•</span>
                        Reason: <span className="text-slate-700">{item.reason}</span>
                    </>
                ) : null}
            </div>
        </div>
    );
}


// -----------------------------------------------------------------------
// Iter63 — Schedule bonus line item. Rendered under the aggregate
// "Schedule bonus" subtotal so managers can see the per-day breakdown
// with each override's specific label + rate.
//
// Format:  "{label} — {h}h {m}m × 1x @ ${rate}/hr = ${amount}"
// Caption: "Per shift rule override on {work_date}"
// -----------------------------------------------------------------------
function ScheduleBonusLine({ item, testid }) {
    const mins = Number(item.minutes) || 0;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const rate = Number(item.hourly_rate) || 0;
    const amount = Number(item.amount) || 0;
    const label = item.label || "Schedule bonus";
    return (
        <div
            className="border-t px-3 py-1.5 bg-slate-50/40 text-[11px] leading-tight"
            data-testid={testid}
        >
            <div className="flex items-center justify-between mono">
                <span className="text-slate-700">
                    <strong>{label}</strong>
                    {" — "}{h}h {m}m × 1x @ ${rate.toFixed(2)}/hr
                </span>
                <span className="asl-navy-text font-semibold">= {fmt(amount)}</span>
            </div>
            <div className="text-[10px] text-[color:var(--asl-muted)] mt-0.5">
                Per shift rule override on{" "}
                <span className="asl-navy-text">{item.work_date || "—"}</span>
            </div>
        </div>
    );
}
